import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react'
import { BlackActionButton } from '../buttons/BlackActionButton'
import { useState } from 'react'
import { approvePendingSubmission } from '../../pocketbase/PocketbaseApprovedSubmissionsService'

interface IApproveSubmissionDialogProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    submissionId: string | null
    title?: string
    content?: string
    confirmLabel?: string
    cancelLabel?: string
}

export const ApproveSubmissionDialog = (props: IApproveSubmissionDialogProps) => {
    const {
        isOpen,
        onClose,
        onSuccess,
        submissionId,
        title = 'Approve Submission',
        content = 'Are you sure you want to approve this submission? This will create an approved submission and delete the pending submission.',
        confirmLabel = 'Approve',
        cancelLabel = 'Cancel'
    } = props

    const [isApproving, setIsApproving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleConfirm = async () => {
        if (!submissionId) return

        try {
            setIsApproving(true)
            setError(null)

            await approvePendingSubmission(submissionId)

            // Call success callback
            onSuccess()

            // Close dialog
            onClose()
        } catch (error: any) {
            console.error('Error approving submission:', error)

            // Extract error message
            let errorMessage = 'Failed to approve submission. Please try again.'
            if (error?.message) {
                errorMessage = error.message
            } else if (error?.data?.message) {
                errorMessage = error.data.message
            } else if (typeof error === 'string') {
                errorMessage = error
            }

            setError(errorMessage)
        } finally {
            setIsApproving(false)
        }
    }

    const handleClose = () => {
        if (!isApproving) {
            setError(null)
            onClose()
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/30" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <DialogTitle className="text-lg font-semibold text-gray-900">
                        {title}
                    </DialogTitle>

                    <div className="mt-3 text-gray-600">
                        {content}
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isApproving}
                            className={`px-4 py-2 text-sm font-medium ${isApproving
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:text-gray-900 cursor-pointer'
                                }`}
                        >
                            {cancelLabel}
                        </button>
                        <div onClick={isApproving ? undefined : handleConfirm}>
                            <BlackActionButton
                                label={isApproving ? 'Approving...' : confirmLabel}
                            />
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
